import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Contrato } from '../types';
import contractsService from '../services/contracts.service';

interface PagoFormData {
  contratoId: number;
  monto: number;
  fecha: string;
  descripcion: string;
}

const PagoForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const contratoId = searchParams.get('contratoId');
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [contratos, setContratos] = useState<Contrato[]>([]);
  const [montoFormatted, setMontoFormatted] = useState('');
  const [formData, setFormData] = useState<PagoFormData>({
    contratoId: contratoId ? parseInt(contratoId) : 0,
    monto: 0,
    fecha: new Date().toISOString().split('T')[0],
    descripcion: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const contratosData = await contractsService.getContratos(1, 1000);
        setContratos(contratosData.contratos || []);

        if (isEditing && id) {
          const pago = await contractsService.getPagoProgramadoById(parseInt(id));
          setFormData({
            contratoId: pago.contratoId,
            monto: pago.monto,
            fecha: pago.fecha.split('T')[0],
            descripcion: pago.descripcion || '',
          });
          
          // Formatear el monto inicial
          setMontoFormatted(formatNumber(pago.monto.toString()));
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        alert('Error al cargar los datos');
      }
    };

    fetchData();
  }, [id, isEditing]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'contratoId' ? parseInt(value) : 
               name === 'monto' ? parseFloat(value) || 0 : value
    }));
  };

  // Funciones para formateo de números
  const formatNumber = (value: string): string => {
    // Remover caracteres no numéricos excepto punto decimal
    const cleanValue = value.replace(/[^\d.]/g, '');
    
    // Si está vacío, retornar vacío
    if (!cleanValue) return '';
    
    // Separar parte entera y decimal
    const parts = cleanValue.split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1];
    
    // Formatear parte entera con separadores de miles
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    
    // Reconstruir el número
    return decimalPart !== undefined ? `${formattedInteger}.${decimalPart}` : formattedInteger;
  };

  const parseFormattedNumber = (formattedValue: string): number => {
    // Remover separadores de miles y convertir a número
    const cleanValue = formattedValue.replace(/,/g, '');
    const parsed = parseFloat(cleanValue);
    return isNaN(parsed) ? 0 : parsed;
  };

  const handleMontoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const formattedValue = formatNumber(inputValue);
    const numericValue = parseFormattedNumber(formattedValue);
    
    setMontoFormatted(formattedValue);
    setFormData(prev => ({ ...prev, monto: numericValue }));
  };

  const handleMontoBlur = () => {
    // Asegurar que el valor esté formateado al perder el foco
    const formattedValue = formatNumber(montoFormatted);
    setMontoFormatted(formattedValue);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Asegurar que el monto esté actualizado con el último valor formateado
      const finalMonto = parseFormattedNumber(montoFormatted);
      
      if (isEditing && id) {
        // Para actualizaciones, no enviar contratoId ya que no debe cambiar
        const updateData = {
          monto: finalMonto,
          fecha: new Date(formData.fecha).toISOString(),
          descripcion: formData.descripcion,
        };
        await contractsService.updatePagoProgramado(parseInt(id), updateData);
      } else {
        // Para creación, incluir contratoId
        const createData = {
          contratoId: formData.contratoId,
          monto: finalMonto,
          fecha: new Date(formData.fecha).toISOString(),
          descripcion: formData.descripcion,
        };
        await contractsService.createPagoProgramado(createData);
      }
      navigate('/pagos');
    } catch (error) {
      console.error('Error saving pago:', error);
      alert('Error al guardar el pago');
    } finally {
      setLoading(false);
    }
  };

  const selectedContrato = contratos.find(c => c.id === formData.contratoId);

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/pagos')}
          className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Volver a pagos
        </button>
        <h1 className="text-3xl font-bold text-gray-900">
          {isEditing ? 'Editar Pago Programado' : 'Registrar Nuevo Pago Programado'}
        </h1>
        <p className="mt-2 text-sm text-gray-700">
          {isEditing ? 'Actualiza la información del pago programado' : 'Registra un nuevo pago programado en el cronograma'}
        </p>
      </div>

      {/* Form */}
      <div className="max-w-4xl">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Información del Pago */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">
              Información del Pago Programado
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label htmlFor="contratoId" className="block text-sm font-medium text-gray-700 mb-2">
                  Contrato *
                </label>
                <select
                  id="contratoId"
                  name="contratoId"
                  required
                  value={formData.contratoId}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value={0}>Selecciona un contrato</option>
                  {contratos.map(contrato => (
                    <option key={contrato.id} value={contrato.id}>
                      {contrato.numeroContrato} - {contrato.cliente?.nombre}
                    </option>
                  ))}
                </select>
                {selectedContrato && (
                  <p className="mt-2 text-sm text-gray-600">
                    Valor del contrato: ${selectedContrato.valorTotal.toLocaleString('es-CO')}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="monto" className="block text-sm font-medium text-gray-700 mb-2">
                  Monto Programado *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">$</span>
                  <input
                    type="text"
                    id="monto"
                    name="monto"
                    required
                    value={montoFormatted}
                    onChange={handleMontoChange}
                    onBlur={handleMontoBlur}
                    placeholder="0"
                    className="block w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="fecha" className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha Programada *
                </label>
                <input
                  type="date"
                  id="fecha"
                  name="fecha"
                  required
                  value={formData.fecha}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

            </div>

            <div className="mt-6">
              <label htmlFor="descripcion" className="block text-sm font-medium text-gray-700 mb-2">
                Descripción
              </label>
              <textarea
                id="descripcion"
                name="descripcion"
                rows={4}
                value={formData.descripcion}
                onChange={handleInputChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Descripción del pago programado (ej. Cuota 1 de 12, Anticipo, etc.)"
              />
            </div>
          </div>

          {/* Summary Card */}
          {selectedContrato && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-blue-900 mb-4">Resumen del Contrato</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <dt className="text-sm font-medium text-blue-700">Cliente</dt>
                  <dd className="mt-1 text-sm text-blue-900">{selectedContrato.cliente?.nombre}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-blue-700">Valor Total</dt>
                  <dd className="mt-1 text-sm text-blue-900 font-semibold">
                    ${selectedContrato.valorTotal.toLocaleString('es-CO')}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-blue-700">Estado</dt>
                  <dd className="mt-1">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        selectedContrato.estado === 'vigente'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {selectedContrato.estado}
                    </span>
                  </dd>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/pagos')}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || formData.contratoId === 0}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Registrar Pago Programado')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PagoForm;